import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LabelBadge } from "@/components/LabelBadge";
import { ArrowRight, HelpCircle } from "lucide-react";

function QuizCard({ quiz }) {
  const accent = quiz.encyclopedia === "zyfley" ? "var(--zyfley)" : "var(--ghoulax)";
  const tint = quiz.encyclopedia === "zyfley" ? "var(--zyfley-soft)" : "var(--ghoulax-soft)";
  return (
    <Link
      to={`/quiz/${quiz.slug}`}
      data-testid={`quiz-card-${quiz.slug}`}
      className="group flex flex-col rounded-md border-2 p-6 shadow-sm transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md"
      style={{ backgroundColor: "var(--arc-card)", borderColor: accent }}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: tint }}>
          <HelpCircle size={20} style={{ color: accent }} />
        </div>
        {quiz.label && <LabelBadge label={quiz.label} />}
      </div>
      <h3 className="mt-4 font-serif-arc text-xl font-bold group-hover:underline">{quiz.title}</h3>
      {quiz.subtitle && <p className="mt-2 flex-1 text-sm italic leading-relaxed" style={{ color: "var(--arc-text-2)" }}>{quiz.subtitle}</p>}
      <p className="mt-4 text-xs" style={{ color: "var(--arc-text-2)" }}>{quiz.questions?.length || 0} questions</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: accent }}>Take the quiz <ArrowRight size={15} /></span>
    </Link>
  );
}

export default function QuizHub() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/quizzes").then((r) => setQuizzes(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const ghoulax = quizzes.filter((q) => q.encyclopedia === "ghoulax");
  const zyfley = quizzes.filter((q) => q.encyclopedia === "zyfley");

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <header className="text-center">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--arc-text-2)" }}>The Archives</p>
        <h1 className="mt-2 font-serif-arc text-3xl font-black tracking-tight sm:text-4xl" data-testid="quiz-hub-title">Quizzes</h1>
        <p className="mx-auto mt-3 max-w-lg text-sm" style={{ color: "var(--arc-text-2)" }}>Choose a quiz to test your knowledge — or your bias.</p>
      </header>

      {loading && <p className="mt-10 text-center text-sm" style={{ color: "var(--arc-text-2)" }}>Loading quizzes…</p>}
      {!loading && quizzes.length === 0 && (
        <p className="mt-10 text-center text-sm" style={{ color: "var(--arc-text-2)" }}>No quizzes are available yet.</p>
      )}

      {ghoulax.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-4 font-serif-arc text-lg font-bold" style={{ color: "var(--ghoulax)" }}>Ghoulax Archives</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">{ghoulax.map((q) => <QuizCard key={q.slug} quiz={q} />)}</div>
        </section>
      )}

      {zyfley.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-4 font-serif-arc text-lg font-bold" style={{ color: "var(--zyfley)" }}>Zyfley Encyclopedia</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">{zyfley.map((q) => <QuizCard key={q.slug} quiz={q} />)}</div>
        </section>
      )}
    </div>
  );
}
